import React, { useMemo } from 'react'
import { Form, Input, Button, Space, Checkbox, Spin } from 'antd'
import { Copy } from 'lucide-react'
import { useFFEStore } from '../../store/ffeStore'
import { OrderItemList } from './OrderItemList'
import { OrderTotals } from './OrderTotals'
import {
  CompletedPurchaseOrder,
  DraftPurchaseOrder
} from '../../types/purchaseOrder'
import { useApi } from '../../services/ApiContext'
import { SupplierOrderItemList } from './PurchaseOrderDrawer'
import { useCompanyStore } from '../../store/company/companyStore'

interface Props {
  group: {
    supplierId: string
    items: SupplierOrderItemList
    subtotal: number
    tax: number
    total: number
    taxRate: number
  }
  supplierIdsInGroups: Array<string>
  onChangeItemSelection: (index: string, selected: boolean) => void
}

export const SupplierOrderDetails: React.FC<Props> = ({
  group,
  supplierIdsInGroups,
  onChangeItemSelection
}) => {
  const purchaseOrders = useFFEStore(
    (state) => state.document.purchaseOrders || []
  )
  const supplier = useFFEStore((state) =>
    state.suppliers.find((s) => s._id === group.supplierId)
  )
  const documentCloudId = useFFEStore((state) => state.document._id)
  const documentLocalId = useFFEStore((state) => state.document.id)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const updateLineItemMaterialStatus = useFFEStore(
    (state) => state.updateLineItemMaterialStatus
  )
  const internalDescriptionVisible = useCompanyStore(
    (state) => state.company.purchaseOrderSettings?.internalDescriptionVisible
  )
  const patchPurchaseOrder = useFFEStore((state) => state.patchPurchaseOrder)
  const setPurchaseOrders = useFFEStore((state) => state.setPurchaseOrders)
  const purchaseOrderSettings = useFFEStore(
    (state) => state.document.purchaseOrderSettings
  )
  const api = useApi()

  const [loadingCompletedOrder, setLoadingCompletedOrder] =
    React.useState(false)

  const po = useMemo<DraftPurchaseOrder>(
    () =>
      purchaseOrders.find((po) => po.supplier === group.supplierId) || {
        supplier: group.supplierId,
        id: crypto.randomUUID()
      },
    [purchaseOrders, group.supplierId]
  )
  const [markAsOrdered, setMarkAsOrdered] = React.useState(true)

  if (!po) return null

  const handleCopyField = (
    field: 'deliveryAddress' | 'deliveryInstructions'
  ) => {
    const value = po[field]
    if (!value) return

    // Update all other POs that don't have this field set
    for (const purchaseOrderSupplierId of supplierIdsInGroups) {
      console.log(
        `Copying ${field} from ${po.supplier} to ${purchaseOrderSupplierId}`)
      if (purchaseOrderSupplierId !== group.supplierId) {
        patchPurchaseOrder(purchaseOrderSupplierId, { [field]: value })
      }
    }
  }

  const loadPurchaseOrders = async () => {
    if (documentCloudId) {
      const purchaseOrders = await api.loadPurchaseOrders(documentCloudId)
      setPurchaseOrders(purchaseOrders)
      return
    }
  }

  const handleExport = async () => {
    setLoadingCompletedOrder(true)
    const poPayload: Omit<CompletedPurchaseOrder, '_id'> = {
      id: crypto.randomUUID(),
      poNumber: po.poNumber ?? '',
      supplier: {
        _id: group.supplierId,
        name: supplier?.name ?? '',
        address: supplier?.address ?? ''
      },
      deliveryAddress: po.deliveryAddress ?? '',
      quoteReference: po.quoteReference,
      date: new Date().toISOString(),
      items: group.items
        .filter((item) => item.selected)
        .map((item) =>
          item.type === 'lineItem'
            ? {
                type: 'lineItem',
                id: item.id,
                code: item.code,
                supplierItemCode: item.supplierItemCode ?? '',
                quantity: item.quantity,
                costPrice: item.costPrice,
                total: item.costPrice * item.quantity,
                internalNotes: internalDescriptionVisible ? item.internalNotes : undefined
              }
            : {
                type: 'material',
                id: item.id,
                code:
                  item.lineItem.code +
                  ` - ${item.materialType === 'finish' ? 'Finish' : 'Accessory'}`,
                supplierItemCode: item.supplierItemCode ?? '',
                materialId: item.materialId,
                quantity: item.quantity,
                costPrice: item.costPerUnit,
                name: item.name,
                total: item.costPerUnit * item.quantity,
                unit: item.unit
              }
        ),
      deliveryInstructions: po.deliveryInstructions ?? '',
      contact: {
        name: purchaseOrderSettings.contact.name,
        email: purchaseOrderSettings.contact.email,
        phone: purchaseOrderSettings.contact.phone
      },
      subtotal: group.subtotal,
      tax: group.tax,
      total: group.total,
      taxRate: group.taxRate,
      documentId: documentCloudId ?? documentLocalId
    }

    // const response = await Promise.all([
    const purchaseOrderResponse = await api.createPurchaseOrder(poPayload)
    // api.downloadPurchaseOrderPDF(company, poPayload)
    // ])

    // FileSaver.saveAs(
    //   response[1],
    //   `PO-${po.poNumber ? po.poNumber + '-' : ''}${new Date().toLocaleDateString()}.pdf`
    // )
    await loadPurchaseOrders()

    if (markAsOrdered) {
      // Update status of all items to 'ordered'
      for (const item of group.items.filter((item) => item.selected)) {
        if (item.type === 'lineItem') {
          updateLineItem(item.id, { status: 'ordered' })
        }
        if (item.type === 'material') {
          // find line item with that material and update status
          if (item.lineItem) {
            updateLineItemMaterialStatus(
              item.lineItem.id,
              item.materialId,
              'ordered'
            )
          }
        }
      }
    }
    for (const item of group.items.filter((item) => item.selected)) {
      if (item.type === 'lineItem') {
        updateLineItem(item.id, { purchaseOrderId: purchaseOrderResponse.id })
      }
      if (item.type === 'material') {
        // find line item with that material and update status
        if (item.lineItem) {
          updateLineItem(item.lineItem.id, {
            purchaseOrderId: purchaseOrderResponse.id
          })
        }
      }
    }
    setLoadingCompletedOrder(false)
  }

  return (
    <Spin tip={'Preparing document...'} spinning={loadingCompletedOrder}>
      <div className="space-y-6">
        <Form layout="vertical">
          <Space className="w-full">
            <Form.Item label="PO Number" className="flex-1">
              <Input
                value={po.poNumber}
                onChange={(e) =>
                  patchPurchaseOrder(group.supplierId, {
                    poNumber: e.target.value
                  })
                }
                placeholder="Enter PO number"
              />
            </Form.Item>

            <Form.Item label="Quote Reference" className="flex-1">
              <Input
                value={po.quoteReference}
                onChange={(e) =>
                  patchPurchaseOrder(group.supplierId, {
                    quoteReference: e.target.value
                  })
                }
                placeholder="Enter quote reference"
              />
            </Form.Item>
          </Space>

          <Form.Item label="Delivery Address">
            <Input.TextArea
              value={po.deliveryAddress}
              onChange={(e) =>
                patchPurchaseOrder(group.supplierId, {
                  deliveryAddress: e.target.value
                })
              }
              placeholder="Enter delivery address"
              rows={3}
              className="mb-2"
            />
            <Button
              type="text"
              size="small"
              icon={<Copy className="h-4 w-4" />}
              onClick={() => handleCopyField('deliveryAddress')}
            >
              Copy to other suppliers
            </Button>
          </Form.Item>

          <Form.Item label="Delivery Instructions">
            <Input.TextArea
              value={po.deliveryInstructions}
              onChange={(e) =>
                patchPurchaseOrder(group.supplierId, {
                  deliveryInstructions: e.target.value
                })
              }
              placeholder="Enter delivery instructions"
              rows={3}
              className="mb-2"
            />
            <Button
              type="text"
              size="small"
              icon={<Copy className="h-4 w-4" />}
              onClick={() => handleCopyField('deliveryInstructions')}
            >
              Copy to other suppliers
            </Button>
          </Form.Item>
        </Form>

        <OrderItemList
          items={group.items}
          onChangeItemSelection={onChangeItemSelection}
        />
        <OrderTotals
          subtotal={group.subtotal}
          tax={group.tax}
          total={group.total}
        />
        <Space className="flex justify-end">
          <Checkbox
            checked={markAsOrdered}
            onChange={(e) => setMarkAsOrdered(e.target.checked)}
          >
            Mark items as ordered
          </Checkbox>
          <Button type="primary" onClick={handleExport}>
            Complete Purchase Order
          </Button>
        </Space>
      </div>
    </Spin>
  )
}
